import {includes, sortBy} from '@shared/helpers/collection'
import {formatISOByGuard} from '@shared/helpers/date'
import {SchemasOrderDetail} from '@shared/schemas/order'

import {AssignmentCode, ExposureStatus, ExposureStatusType} from '@web/core/constants'
import {getInflowUrl} from '@web/core/urls'
import {decodeHtmlEntities as decode} from '@web/helpers/htmlEntities'
import {getDeliveryTag, getPatternedRegExp} from '@web/helpers/policies'

import {Assignment} from '../interfaces'
import {Product} from '../interfaces/product'

/** 상품주문 모델 (main) */
export type ProductAPI = SchemasOrderDetail['/']['response']['productOrders'][0]

const convertClaim = (claim: NonNullable<ProductAPI['claim']>) => {
    return {
        no: claim.claimNo,
        type: claim.claimType,
        status: claim.claimStatusType,
        requestReason: claim.claimRequestReason,
        requestDetailContent: decode(claim.claimRequestDetailContent),
        deliveryFeePayAmount: claim.claimDeliveryFeePayAmount,
        extraFeePayAmount: claim.extraFeePayAmount,
        holdbackClassType: claim.holdbackClassType,
        holdbackDetailContent: decode(claim.holdbackDetailContent),
        refundFailReason: claim.refundFailReason,
        refundTreatmentDateTime: formatISOByGuard(claim.refundTreatmentDateTime),
        quantity: claim.claimRequestQuantity,
        membershipsArrivalGuaranteeClaimSupportingAmount: claim.membershipsArrivalGuaranteeClaimSupportingAmount,
    }
}

export const getProducts = (products: ProductAPI[], isMobileDisplay: boolean, admissionDate: string): Product[] => {
    return products.map((product) => {
        const urlWithInflow = getInflowUrl({
            no: product.merchantNo.toString(),
            tr: 'ppc',
            pType: isMobileDisplay ? 'M' : 'P',
            retUrl: product?.productUrl ?? '',
        })

        /** 20240418 과 같은 형식으로 내려오는 date를 parsing 하기 위한 정규식 */
        const dateRegExp = getPatternedRegExp({pattern: [[4], [2], [2]]})
        /** 0200 과 같은 형식으로 내려오는 time을 parsing 하기 위한 정규식 */
        const timeRegExp = getPatternedRegExp({pattern: [[2], [2]]})

        return {
            largeCategoryId: product?.largeCategoryId ? product.largeCategoryId : undefined,
            middleCategoryId: product?.middleCategoryId ? product.middleCategoryId : undefined,
            smallCategoryId: product?.smallCategoryId ? product.smallCategoryId : undefined,
            orderNo: product.productOrderNo,
            productId: product.productNo,
            url: urlWithInflow,
            imageUrl: product?.productImageUrl,
            name: product.productName,
            bundleGroupKey: product.bundleGroupKey,
            merchantNo: product.merchantNo,
            exposureText: product?.exposureText,
            freeGiftName: product?.freeGiftName,
            ecNvMid: product?.ecNvMid,
            productCategoryId: product?.productCategoryId,
            payDateTime: formatISOByGuard(product?.payDateTime),
            reviewWritten: product?.reviewWritten,
            delayRewardYn: product.delayRewardYn,
            installationProduct: product.installationProduct,
            status: {
                orderType: product.productOrderStatusType,
                naverBenefit: product.naverBenefit,
                exposureType: product.exposureStatusType,
                customOrderDetailType: product?.customOrderDetailType,
                classType: product.productClassType,
                isPurchaseDecisionExtensionDetailShow: product.exposurePurchaseDecisionExtensionDetail,
                isEcoupon: product.ecoupon,
                ecouponStatus: product.eCouponStatusType,
            },
            delivery: {
                attributeType: product.deliveryAttributeType,
                tagType: getDeliveryTag({
                    arrivalDate: product.arrivalGuaranteeDateTime,
                    deliveryTagType: product.deliveryTagType,
                    deliveryAttributeType: product.deliveryAttributeType,
                    orderDate: admissionDate,
                }),
                prediction: product.deliveryPrediction
                    ? {
                          date: formatISOByGuard(product.deliveryPrediction?.predictionDate) ?? '',
                          probability: product.deliveryPrediction?.prediction,
                      }
                    : undefined,
                hopeDelivery: product.hopeDelivery
                    ? {
                          region: product.hopeDelivery?.region,
                          additionalDeliveryFee: product.hopeDelivery?.additionalDeliveryFee,
                          date: product.hopeDelivery?.hopeDeliveryDate
                              ?.replaceAll('-', '')
                              .replace(dateRegExp, '$1-$2-$3'),
                          time: product.hopeDelivery?.hopeDeliveryTime
                              ?.replaceAll('-', '')
                              .replace(timeRegExp, '$1:$2'),
                          isChangeable: product.hopeDelivery?.hopeDeliveryChangeable,
                          treatmentPerson: product.hopeDelivery?.hopeDeliveryTreatmentPersonType,
                          reason: product.hopeDelivery?.hopeDeliveryChangeReason,
                          beforeDate: product.hopeDelivery?.beforeHopeDeliveryDate
                              ?.replaceAll('-', '')
                              .replace(dateRegExp, '$1-$2-$3'),
                          beforeTime: product.hopeDelivery?.beforeHopeDeliveryTime
                              ?.replaceAll('-', '')
                              .replace(timeRegExp, '$1:$2'),
                      }
                    : undefined,
                completeDateTime: formatISOByGuard(product.deliveryCompleteDateTime),
                arrivalGuaranteeDateTime: formatISOByGuard(product?.arrivalGuaranteeDateTime),
                dispatchDueDateTime: formatISOByGuard(product?.dispatchDueDateTime),
                purchaseDecisionExpectDateTime: formatISOByGuard(product?.purchaseDecisionExpectDateTime),
                purchaseDecisionDateTime: formatISOByGuard(product?.purchaseDecisionDateTime),
                methodType: product?.deliveryMethodType,
                deliveryKeeperRegistered: product?.deliveryKeeperRegistered,
            },
            preOrder: product.preOrder
                ? {
                      deliveryStartDateTime: formatISOByGuard(product.preOrder.deliveryStartDateTime) ?? null,
                      deliveryEndDateTime: formatISOByGuard(product.preOrder.deliveryEndDateTime) ?? null,
                      saleStartDateTime: formatISOByGuard(product.preOrder.saleStartDateTime) ?? null,
                      saleEndDateTime: formatISOByGuard(product.preOrder.saleEndDateTime) ?? null,
                      minimumOrderQuantity: product.preOrder.minimumOrderQuantity ?? null,
                  }
                : undefined,
            price: {
                orderAmount: product.orderAmount,
                productAmount: product.productAmount,
                orderQuantity: product?.orderQuantity,
                initOrderQuantity: product?.initOrderQuantity,
                optionContents: product?.optionContents,
                optionAmount: product?.optionAmount,
                // 할인 적용되기 전 가격 - 할인 적용된 가격
                discountAmount: product.productAmount - product.orderAmount,
            },
            giftingReceiver: product.giftingReceiver
                ? {
                      receivingStatus: product.giftingReceiver.giftReceivingStatusType,
                      receiverName: product.giftingReceiver?.receiverName,
                      receiverKeyType: product.giftingReceiver?.giftingReceiverKeyType,
                      maskedReceiverId: product.giftingReceiver?.maskedReceiverId,
                      maskedReceiverPhoneNumber: product.giftingReceiver?.maskedReceiverPhoneNumber,
                  }
                : undefined,
            branch: product.branch
                ? {
                      benefitQuantityStandard: product.branch?.branchBenefitApplyValue,
                      benefitQuantity: product.branch?.branchBenefitValue,
                      name: product.branch?.branchName,
                      deliveryData: product.branch?.branchDeliveryDate,
                      isDeliverySlotNeeded: product.branch?.useBranchDeliverySlot,
                      deliveryStartTime: product.branch?.branchDeliveryStartTime,
                      deliveryEndTime: product.branch?.branchDeliveryEndTime,
                      deliveryPreparationMinutes: product.branch?.branchDeliveryPreparationMinutes,
                      deliveryDurationMinutes: product.branch?.branchDeliveryDurationMinutes,
                      alternativeProductAccepted: product.branch?.alternativeProductAccepted,
                      marketType: product.branch?.branchMarketType,
                      orderDeliveryTag: product.branch?.orderDeliveryTagType,
                  }
                : undefined,
            claim: product.claim ? convertClaim(product.claim) : undefined,
            prevClaim: product.beforeClaim ? convertClaim(product.beforeClaim) : undefined,
            quantityClaims:
                product.quantityClaims && product.quantityClaims.length > 0
                    ? product.quantityClaims.map((quantityClaim) => {
                          return {
                              exposureType: quantityClaim.exposureStatusType,
                              exposureText: quantityClaim.exposureText,
                              orderAmount: quantityClaim.orderAmount,
                              productAmount: quantityClaim.productAmount,
                              claim: convertClaim(quantityClaim.claim),
                              prevClaim: quantityClaim.beforeClaim
                                  ? convertClaim(quantityClaim.beforeClaim)
                                  : undefined,
                          }
                      })
                    : undefined,
            talkChannelId: product?.fleaMarket?.talkChannelId,
            cafeName: product?.fleaMarket?.cafeName,
        }
    })
}

const claimProcessEmphasizeCodes = [AssignmentCode.TRACK_DELIVERY, AssignmentCode.TRACK_RETURN_DELIVERY]

const reviewEmphasizeCodes = [AssignmentCode.WRITE_REVIEW, AssignmentCode.WRITE_AFTER_USE_REVIEW]

const changeIndexToFront = <T>(array: Array<T>, callback: (value: T) => boolean) => {
    return [...array.filter((value) => callback(value)), ...array.filter((value) => !callback(value))]
}

const getSort = (assignment: Assignment) =>
    ['장바구니 담기', '바로 구매하기'].findIndex((text) => assignment.text === text) ?? -1

const getAssignmentsSort = (assignments: Assignment[]) => {
    return sortBy(getSort, assignments)
}

type GetAssignmentsType = {
    rawAssignments?: Assignment[]
    exposureType?: ExposureStatusType
    deliveryKeeperRegistered?: boolean
}

/**
 * 특정 배송 상태의 경우 과업버튼의 순서와 강조되는 버튼의 종류에 대해 BE에서 내려주는 데이터와 이격이 있어서 순서 조정이 필요
 * see) https://wiki.fin.navercorp.com/pages/viewpage.action?pageId=147850843
 */
export const getAssignments = ({rawAssignments = [], exposureType, deliveryKeeperRegistered}: GetAssignmentsType) => {
    let assignments: Assignment[] = [...rawAssignments]

    if (!exposureType) {
        // do nothing
        /** 배송중 / 배송준비중 case - 배송트래킹이 가능한 경우 '배송조회'를 첫번째로 노출 + 강조 */
    } else if (
        (exposureType === ExposureStatus.배송중 || exposureType === ExposureStatus.배송준비중) &&
        deliveryKeeperRegistered
    ) {
        assignments = changeIndexToFront(assignments, (value) => value.code === AssignmentCode.TRACK_DELIVERY)
        /** 반품환불완료 case - '재배송조회' / '반송조회' 를 첫번째로 노출 + 강조 */
    } else if (exposureType === ExposureStatus.반품환불완료) {
        assignments = changeIndexToFront(assignments, (value) => includes(value.code, claimProcessEmphasizeCodes))
        /** 교환완료인 경우 - '리뷰쓰기' / '한달리뷰쓰기' 를 첫번째로 노출 + 강조 */
    } else if (exposureType === ExposureStatus.교환완료) {
        assignments = changeIndexToFront(assignments, (value) => includes(value.code, reviewEmphasizeCodes))
        /** 반품중 / 반품수거완료 / 반품요청 의 경우 - '반품비용결제' 를 첫번째로 노출 + 강조 */
    } else if (
        exposureType === ExposureStatus.반품중 ||
        exposureType === ExposureStatus.반품수거완료 ||
        exposureType === ExposureStatus.반품요청
    ) {
        assignments = changeIndexToFront(assignments, (value) => value.code === AssignmentCode.PAY_REFUND_CHARGE)
        /** 교환중 / 교환수거완료 / 교환요청 의 경우 - '교환비용결제' 를 첫번째로 노출 + 강조 */
    } else if (
        exposureType === ExposureStatus.교환중 ||
        exposureType === ExposureStatus.교환수거완료 ||
        exposureType === ExposureStatus.교환요청
    ) {
        assignments = changeIndexToFront(assignments, (value) => value.code === AssignmentCode.PAY_EXCHANGE_CHARGE)
    }

    return getAssignmentsSort(assignments)
}
